import React from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ArrowRight, Code, Book, Key, Terminal, Webhook, FileJson } from 'lucide-react';
import SEO from '../components/SEO';

const RESOURCE_KEYS = [
  { key: 'api_docs',   icon: Book,     link: '#api-overview',   internal: true },
  { key: 'auth',       icon: Key,      link: '#authentication', internal: true },
  { key: 'quickstart', icon: Terminal, link: '#quickstart',     internal: true },
  { key: 'webhooks',   icon: Webhook,  link: '/integrations/webhooks', internal: false },
  { key: 'reference',  icon: FileJson, link: '#endpoints',      internal: true },
  { key: 'examples',   icon: Code,     link: '#examples',       internal: true },
];

const CODE = <code className="text-amber-400 bg-white/5 px-1.5 py-0.5 rounded" />;
const QUICKSTART = [0, 1, 2, 3, 4];

const DevelopersPage = () => {
  const { t } = useTranslation('landing');
  const resources = RESOURCE_KEYS;

  const codeExample = `// Create a task via the BusyBee.Day API
const response = await fetch('https://api.busybee.day/api/tasks', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_ACCESS_TOKEN',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    title: 'Review pull request',
    priority: 'p2',
    due_date: '2026-11-03',
    duration: 30,
    project_id: 'YOUR_PROJECT_ID'
  })
});

const task = await response.json();
console.log(task);`;

  const authExample = `// 1. Log in to get an access token (a JWT, valid for 7 days)
const login = await fetch('https://api.busybee.day/api/auth/login', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    email: 'you@example.com',
    password: 'your-password'
  })
}).then(r => r.json());

// 2. With two-step verification on, trade the challenge for the token
let token = login.access_token;
if (login.mfa_required) {
  const done = await fetch('https://api.busybee.day/api/auth/mfa/challenge', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ challenge_token: login.challenge_token, code: '123456' })
  }).then(r => r.json());
  token = done.access_token;
}

// 3. Send the token with every request
const tasks = await fetch('https://api.busybee.day/api/tasks', {
  headers: { 'Authorization': \`Bearer \${token}\` }
}).then(r => r.json());`;

  const webhookExample = `// Outgoing webhook delivery (task.completed)
{
  "event": "task.completed",
  "event_id": "3f6c2a9e-...",
  "timestamp": "2026-10-30T14:32:11.204511+00:00",
  "actor": { "id": "...", "name": "Anna", "email": "anna@example.com", "avatar_url": null },
  "data": {
    "id": "8f3a51d2-...",
    "title": "Review pull request",
    "priority": "p2",
    "project_id": "...",
    "url": "https://busybee.day/app/tasks?task=8f3a51d2-..."
  }
}

// Headers
// X-BusyBee-Event: task.completed
// X-BusyBee-Delivery: <event_id>
// X-BusyBee-Signature: sha256=<hex>
//   = HMAC-SHA256 of the raw body, keyed with the webhook's secret`;

  // What each endpoint does: developers.endpoints.items in landing.json, in this order.
  const endpoints = [
    { method: 'GET',    path: '/api/tasks' },
    { method: 'POST',   path: '/api/tasks' },
    { method: 'PUT',    path: '/api/tasks/{id}' },
    { method: 'GET',    path: '/api/tasks/occurrences' },
    { method: 'DELETE', path: '/api/tasks/{id}' },
    { method: 'GET',    path: '/api/projects' },
    { method: 'POST',   path: '/api/projects' },
    { method: 'GET',    path: '/api/habits' },
    { method: 'POST',   path: '/api/habits' },
    { method: 'GET',    path: '/api/focus-time' },
    { method: 'POST',   path: '/api/webhooks' },
    { method: 'POST',   path: '/api/ai/parse?text=...' },
  ];

  const methodColor = {
    GET:    'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
    POST:   'text-blue-400 bg-blue-500/10 border-blue-500/30',
    PUT:    'text-amber-400 bg-amber-500/10 border-amber-500/30',
    DELETE: 'text-red-400 bg-red-500/10 border-red-500/30',
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <SEO title={t('developers.seo.title')} description={t('developers.seo.description')} canonical="/developers" />
      <Navbar />

      <main className="pt-20 pb-24">
        {/* Hero */}
        <section className="max-w-7xl mx-auto px-4 py-12 sm:py-16 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-400 mb-6">
            <Code className="w-4 h-4" />
            <span className="text-sm font-medium">{t('developers.badge')}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{t('developers.headline')}</h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
            {t('developers.subtitle')}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/register" className="bg-amber-500 text-black px-6 py-3 rounded-lg font-medium hover:bg-amber-400 transition-colors flex items-center gap-2">
              {t('developers.cta_primary')}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#api-overview" className="border border-gray-700 text-white px-6 py-3 rounded-lg font-medium hover:bg-white/5 transition-colors">
              {t('developers.cta_secondary')}
            </a>
          </div>
        </section>

        {/* Code Example */}
        <section className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-[#1a1a1b] rounded-2xl border border-gray-800 overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-800">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-gray-500 text-sm ml-2">example.js</span>
            </div>
            <pre className="p-6 text-sm text-gray-300 overflow-x-auto">
              <code>{codeExample}</code>
            </pre>
          </div>
        </section>

        {/* Resources Grid */}
        <section className="max-w-7xl mx-auto px-4 py-12 sm:py-16">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">{t('developers.resources.headline')}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {resources.map((resource) => {
              const Icon = resource.icon;
              const className = "p-6 bg-white/5 rounded-2xl border border-gray-800 hover:border-amber-500/30 transition-colors group block";
              const inner = (
                <>
                  <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-amber-400" />
                  </div>
                  <h3 className="text-white font-semibold group-hover:text-amber-400 transition-colors mb-2">{t(`developers.resources.items.${resource.key}.title`)}</h3>
                  <p className="text-gray-400 text-sm">{t(`developers.resources.items.${resource.key}.desc`)}</p>
                </>
              );
              return resource.internal ? (
                <a key={resource.key} href={resource.link} className={className}>{inner}</a>
              ) : (
                <Link key={resource.key} to={resource.link} className={className}>{inner}</Link>
              );
            })}
          </div>
        </section>

        {/* API Overview */}
        <section id="api-overview" className="max-w-4xl mx-auto px-4 py-12 sm:py-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-white mb-4">{t('developers.overview.headline')}</h2>
          <p className="text-gray-400 leading-relaxed mb-4">
            <Trans t={t} ns="landing" i18nKey="developers.overview.p1" components={{ code: CODE }} />
          </p>
          <p className="text-gray-400 leading-relaxed">
            <Trans t={t} ns="landing" i18nKey="developers.overview.p2" components={{ code: CODE }} />
          </p>
        </section>

        {/* Authentication */}
        <section id="authentication" className="max-w-4xl mx-auto px-4 py-12 scroll-mt-24">
          <h2 className="text-3xl font-bold text-white mb-4">{t('developers.authentication.headline')}</h2>
          <p className="text-gray-400 leading-relaxed mb-4">
            <Trans t={t} ns="landing" i18nKey="developers.authentication.p1" components={{ code: CODE }} />
          </p>
          <p className="text-gray-400 leading-relaxed mb-6">
            <Trans t={t} ns="landing" i18nKey="developers.authentication.p2" components={{ a: <Link to="/integrations/mcp" className="text-amber-400 hover:text-amber-300" /> }} />
          </p>
          <div className="bg-[#1a1a1b] rounded-2xl border border-gray-800 overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-800">
              <span className="text-gray-500 text-sm">auth.js</span>
            </div>
            <pre className="p-6 text-sm text-gray-300 overflow-x-auto"><code>{authExample}</code></pre>
          </div>
        </section>

        {/* Quickstart */}
        <section id="quickstart" className="max-w-4xl mx-auto px-4 py-12 scroll-mt-24">
          <h2 className="text-3xl font-bold text-white mb-6">{t('developers.quickstart.headline')}</h2>
          <ol className="space-y-4 text-gray-300 list-decimal list-inside">
            {QUICKSTART.map((i) => (
              <li key={i}>
                <Trans
                  t={t}
                  ns="landing"
                  i18nKey={`developers.quickstart.steps.${i}`}
                  components={{ b: <span className="text-white font-medium" />, code: <code className="text-amber-400" />, a: <Link to="/register" className="text-amber-400 hover:text-amber-300" /> }}
                />
              </li>
            ))}
          </ol>
        </section>

        {/* Endpoints reference */}
        <section id="endpoints" className="max-w-4xl mx-auto px-4 py-12 scroll-mt-24">
          <h2 className="text-3xl font-bold text-white mb-6">{t('developers.endpoints.headline')}</h2>
          <div className="space-y-2">
            {endpoints.map((e, i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-white/5 rounded-lg border border-gray-800">
                <span className={`text-xs font-mono font-semibold px-2 py-1 rounded border ${methodColor[e.method]} flex-shrink-0`}>{e.method}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-white font-mono text-sm break-all">{e.path}</div>
                  <div className="text-gray-400 text-sm mt-1">{t(`developers.endpoints.items.${i}`)}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-gray-500 text-sm mt-6">
            <Trans t={t} ns="landing" i18nKey="developers.endpoints.missing" components={{ a: <Link to="/contact" className="text-amber-400 hover:text-amber-300" /> }} />
          </p>
        </section>

        {/* Integration Examples */}
        <section id="examples" className="max-w-4xl mx-auto px-4 py-12 scroll-mt-24">
          <h2 className="text-3xl font-bold text-white mb-6">{t('developers.examples.headline')}</h2>
          <p className="text-gray-400 leading-relaxed mb-6">
            {t('developers.examples.body')}
          </p>
          <div className="bg-[#1a1a1b] rounded-2xl border border-gray-800 overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-800">
              <span className="text-gray-500 text-sm">webhook-payload.json</span>
            </div>
            <pre className="p-6 text-sm text-gray-300 overflow-x-auto"><code>{webhookExample}</code></pre>
          </div>
          <p className="text-gray-400 text-sm mt-4">
            <Trans t={t} ns="landing" i18nKey="developers.examples.more" components={{ a: <Link to="/integrations/webhooks" className="text-amber-400 hover:text-amber-300" /> }} />
          </p>
        </section>

        {/* What you can build */}
        <section className="max-w-4xl mx-auto px-4 py-12 sm:py-16">
          <div className="bg-gradient-to-br from-amber-900/20 to-orange-900/20 rounded-2xl border border-gray-800 p-6 sm:p-8">
            <h2 className="text-2xl font-bold text-white mb-6">{t('developers.what_you_can_build.headline')}</h2>
            <div className="grid md:grid-cols-2 gap-3 text-gray-300">
              <div>&bull; {t('developers.what_you_can_build.items.mcp_assistant')}</div>
              <div>&bull; {t('developers.what_you_can_build.items.scripts')}</div>
              <div>&bull; {t('developers.what_you_can_build.items.chat_alerts')}</div>
              <div>&bull; {t('developers.what_you_can_build.items.forms_to_tasks')}</div>
              <div>&bull; {t('developers.what_you_can_build.items.ci_tasks')}</div>
              <div>&bull; {t('developers.what_you_can_build.items.reports')}</div>
            </div>
          </div>
        </section>

        {/* Support */}
        <section className="max-w-4xl mx-auto px-4 py-8 sm:py-12 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">{t('developers.support.headline')}</h2>
          <p className="text-gray-400 mb-6">{t('developers.support.body')}</p>
          <Link to="/contact" className="text-amber-400 hover:text-amber-300 font-medium">{t('developers.support.cta')}</Link>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default DevelopersPage;
